import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { TimeRangePreset } from '@/components/TimeRangeInput';

/**
 * v0.4 追加7要件⑤ (D-235・§2-2d): 指定ノードで実効化されているシフトパターンを
 * `TimeRangeInput` の `presets` にそのまま渡せる形へ解決する。
 * ⚠️ 「ノード単位で全パターンを一括解決するAPI」は新設せず、既存の
 * `GET /settings/shift-patterns/:id/times?node_id=` をパターン数分呼ぶ (通常数件のため §8-89 型の
 * 懸念には当たらない)。値が解決できない (source_kind='none') パターンはプリセット候補から除外する。
 */
type PatternListItem = { pattern_id: string; label: string };
type ResolvedTime = { value: { start_time: string; end_time: string; break_minutes: number } | null };

export const useShiftPatternPresets = (
  nodeId: string | null | undefined, enabled: boolean,
): { presets: TimeRangePreset[]; isLoading: boolean } => {
  const { data: patternsData, isLoading: patternsLoading } = useQuery<{ patterns: PatternListItem[] }>({
    queryKey: ['shift-patterns'], queryFn: () => apiFetch('/settings/shift-patterns'),
    enabled: enabled && !!nodeId,
  });
  const patterns = patternsData?.patterns ?? [];

  const results = useQueries({
    queries: patterns.map((p) => ({
      queryKey: ['shift-pattern-times', p.pattern_id, nodeId],
      queryFn: () => apiFetch<ResolvedTime>(`/settings/shift-patterns/${p.pattern_id}/times?node_id=${encodeURIComponent(nodeId!)}`),
      enabled: enabled && !!nodeId,
    })),
  });

  const presets = useMemo<TimeRangePreset[]>(() => patterns.flatMap((p, i) => {
    const resolved = results[i]?.data?.value;
    return resolved ? [{
      id: p.pattern_id, label: p.label, startTime: resolved.start_time, endTime: resolved.end_time,
      breakMinutes: resolved.break_minutes,
    }] : [];
  }), [patterns, results]);

  return { presets, isLoading: patternsLoading || results.some((r) => r.isLoading) };
};
